import React, { useContext, useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import mainLogo from "../assets/images/main-logo.png";
import { AuthContext } from "../contexts";
import Accounting from "./accounting/Accounting";
import "../assets/css/admin-panel.css";
import Tippy from "@tippyjs/react";
import { CustomSearchInput, CustomNavbarOption } from "../ui";
import axios from "axios";
import UsersInfos from "./users/Users";
import { CircularProgress } from "@mui/material";
import Hiring from "./hiring/Hiring";
import Inventory from "./inventory/Inventory";
import Roles from "./roles/Roles";
import { ArrowLeftIcon, CaretDownIcon, User, Users, ShieldIcon, SignOutIcon, Key } from "@phosphor-icons/react";
import Tracking from "./tracking/Tracking";
import Commerce from "./commerce/Commerce";
import VersionDisplay from "./VersionDisplay";
import ChristmasToggle from "../components/ChristmasToggle";
import ChristmasLights from "../components/ChristmasLights";
import ChristmasTree from "../components/ChristmasTree";
import ThemeContext from "../contexts/ThemeContext";

const Dashboard = ({ countData }) => {
    const adminLocation = window.location.href.replace(process.env.REACT_APP_UPA_INTERFACE_HOST + "admin/", "");
    const navigate = useNavigate();
    const { user, token, setToken, setUser, refreshDashboard } = useContext(AuthContext);
    const { isChristmasTheme } = useContext(ThemeContext);


    const getDefaultRoute = (modules) => {
        const defaultRoutes = {
            accounting: {
                "dashboard": "accounting/dashboard",
                "cat-produits": "accounting/cat-produits",
                "billing-metas": "accounting/billing-metas",
                "billing-subscriptions": "accounting/billing-subscriptions",
            },
            inventory: {
                customers: "inventory/customers",
                support: "inventory/support",
                domains: "inventory/domains",
            },
            projects: {
                tracking: "projects/tracking",
            },
            hiring: {
                tests: "hiring/tests",
            },
            commerce: {
                proposals: "commerce/proposals",
            },
            users: {
                users: "users/users",
                roles: "users/roles",
                tokens: "users/tokens",
            },
        };

        for (const module of modules) {
            const moduleConfig = defaultRoutes[module.value];

            if (!moduleConfig) continue;

            if (typeof moduleConfig === "string") {
                return moduleConfig;
            }

            if (typeof moduleConfig === "object" && module.submodules) {
                for (const submodule of module.submodules) {
                    if (moduleConfig[submodule]) {
                        return moduleConfig[submodule];
                    }
                }
            }
        }

        return "inventory/customers";
    };
    const [searchData, setSearchData] = useState([]);
    const [popoverVisible, setPopoverVisible] = useState(false);
    const searchInputRef = React.useRef(null);
    const show = () => setPopoverVisible(true);
    const hide = () => setPopoverVisible(false);

    const fetchCustomers = React.useCallback(async () => {
        try {
            const response = await axios.get(process.env.REACT_APP_UPA_API_HOST + "pennylane/customers", {
                headers: { Authorization: "Bearer " + token },
            });
            setSearchData(response.data);
        } catch (error) {
            console.log("Error fetching search data:", error);
        }
    }, [token]);

    React.useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    React.useEffect(() => {
        const handleDataUpdate = () => {
            fetchCustomers();
        };

        const handleDashboardUpdate = () => {
            refreshDashboard();
        };

        const handleKeyDown = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === "k") {
                event.preventDefault();
                if (searchInputRef.current) {
                    searchInputRef.current.focus();
                }
            }
        };

        window.addEventListener("zammadDataUpdated", handleDataUpdate);
        window.addEventListener("groupedSupportUpdated", handleDataUpdate);
        window.addEventListener("metasDataUpdated", handleDashboardUpdate);
        window.addEventListener("subscriptionsDataUpdated", handleDashboardUpdate);
        window.addEventListener("customersDataUpdated", handleDashboardUpdate);
        window.addEventListener("testsDataUpdated", handleDashboardUpdate);
        window.addEventListener("groupsDataUpdated", handleDashboardUpdate);
        window.addEventListener("usersDataUpdated", handleDashboardUpdate);
        window.addEventListener("rolesDataUpdated", handleDashboardUpdate);
        window.addEventListener("souscriptionsDataUpdated", handleDashboardUpdate);
        window.addEventListener("livieDataUpdated", handleDashboardUpdate);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("zammadDataUpdated", handleDataUpdate);
            window.removeEventListener("groupedSupportUpdated", handleDataUpdate);
            window.removeEventListener("metasDataUpdated", handleDashboardUpdate);
            window.removeEventListener("subscriptionsDataUpdated", handleDashboardUpdate);
            window.removeEventListener("customersDataUpdated", handleDashboardUpdate);
            window.removeEventListener("testsDataUpdated", handleDashboardUpdate);
            window.removeEventListener("groupsDataUpdated", handleDashboardUpdate);
            window.removeEventListener("usersDataUpdated", handleDashboardUpdate);
            window.removeEventListener("rolesDataUpdated", handleDashboardUpdate);
            window.removeEventListener("souscriptionsDataUpdated", handleDashboardUpdate);
            window.removeEventListener("livieDataUpdated", handleDashboardUpdate);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [fetchCustomers]);

    const handleDisconnect = () => {
        localStorage.clear();
        setToken(null);
        setUser(null);
    };

    const handleSearchbarSelect = (newValue) => {
        navigate("/admin/inventory/customers/" + newValue.id);
    };

    const popover = (
        <div className="user-options">
            {user.role.modules.some((mod) => mod.value === "users" && mod.submodules.includes("profile")) ? (
                <Link to={`/admin/users/${user.id}`} className="user-options-link" onClick={popoverVisible ? hide : show}>
                    <div className="user-options-item users">
                        <User size={16} weight="bold" />
                        <span>Profil</span>
                    </div>
                </Link>
            ) : null}
            {user.role.modules.some((mod) => mod.value === "users" && mod.submodules.includes("users-list")) ? (
                <Link to="users" className="user-options-link" onClick={popoverVisible ? hide : show}>
                    <div className="user-options-item users">
                        <Users size={16} weight="bold" />
                        <span>Utilisateurs</span>
                    </div>
                </Link>
            ) : null}
            {user.role.modules.some((mod) => mod.value === "roles") ? (
                <Link to="roles" className="user-options-link" onClick={popoverVisible ? hide : show}>
                    <div className="user-options-item roles">
                        <ShieldIcon size={16} weight="bold" />
                        <span>Rôles</span>
                    </div>
                </Link>
            ) : null}
            {user.role.modules.some((mod) => mod.value === "users" && mod.submodules.includes("tokens")) ? (
                <Link to="users/tokens" className="user-options-link" onClick={popoverVisible ? hide : show}>
                    <div className="user-options-item tokens">
                        <Key size={16} weight="bold" />
                        <span>Tokens API</span>
                    </div>
                </Link>
            ) : null}
            <div className="user-info-hr-horizontal"></div>
            <div className="user-options-item logout" onClick={handleDisconnect}>
                <SignOutIcon size={16} weight="bold" />
                <span>Se déconnecter</span>
            </div>
        </div>
    );

    return user ? (
        <main className="c-main">
            {isChristmasTheme && <ChristmasLights />}
            {isChristmasTheme && <ChristmasTree />}
            <nav className="c-main__sidebar">
                <div className="c-main__sidebar-container">
                    <div className="c-main__sidebar-logo">
                        <img src={mainLogo} alt="main-logo" width={130} />
                    </div>
                    <div className="menu-container">
                        {user.role.modules.some((mod) => mod.value === "accounting") ? (
                            <div>
                                <div className="menu-item accounting has-sub-items">
                                    <span>Finances</span>
                                </div>
                                {user.role.modules.some((mod) => mod.value === "accounting" && mod.submodules.includes("dashboard")) ? (
                                    <Link to="accounting/dashboard" className="menu-link">
                                        <div className={"menu-sub-item" + (adminLocation.includes("accounting/dashboard") ? " active" : "")}>
                                            <span>Tableau de bord</span>
                                        </div>
                                    </Link>
                                ) : null}
                                {user.role.modules.some((mod) => mod.value === "accounting" && mod.submodules.includes("cat-produits")) ? (
                                    <Link to="accounting/cat-produits" className="menu-link">
                                        <div className={"menu-sub-item" + (adminLocation.includes("accounting/cat-produits") ? " active" : "")}>
                                            <span>Cat. produits</span>
                                        </div>
                                    </Link>
                                ) : null}
                                {user.role.modules.some((mod) => mod.value === "accounting" && mod.submodules.includes("billing-metas")) ? (
                                    <Link to="accounting/billing-metas" className="menu-link">
                                        <div className={"menu-sub-item" + (adminLocation.includes("accounting/billing-metas") ? " active" : "")}>
                                            <span>Fact. métas</span>
                                            <div className="sub-item-info-count">{countData.metasHotels}</div>
                                        </div>
                                    </Link>
                                ) : null}
                                {user.role.modules.some((mod) => mod.value === "accounting" && mod.submodules.includes("billing-subscriptions")) ? (
                                    <Link to="accounting/billing-subscriptions" className="menu-link">
                                        <div className={"menu-sub-item" + (adminLocation.includes("accounting/billing-subscriptions") ? " active" : "")}>
                                            <span>Abonnements</span>
                                            <div className="sub-item-info-count">{countData.subscriptions}</div>
                                        </div>
                                    </Link>
                                ) : null}
                            </div>
                        ) : null}
                        {user.role.modules.some((mod) => mod.value === "commerce") ? (
                            <div>
                                <div className="menu-item commerce has-sub-items">
                                    <span>Commerce</span>
                                </div>
                                {user.role.modules.some((mod) => mod.value === "commerce" && mod.submodules.includes("proposals")) ? (
                                    <Link to="commerce/proposals" className="menu-link">
                                        <div className={"menu-sub-item" + (adminLocation.includes("commerce/proposals") ? " active" : "")}>
                                            <span>Propositions</span>
                                        </div>
                                    </Link>
                                ) : null}
                            </div>
                        ) : null}
                        {user.role.modules.some((mod) => mod.value === "inventory") ? (
                            <div>
                                <div className="menu-item inventory has-sub-items">
                                    <span>Inventaire</span>
                                </div>
                                {user.role.modules.some((mod) => mod.value === "inventory" && mod.submodules.includes("customers")) ? (
                                    <Link to="/admin/inventory/customers" className="menu-link">
                                        <div className={"menu-sub-item" + (adminLocation.includes("inventory/customers") ? " active" : "")}>
                                            <span>Clients</span>
                                            <div className="sub-item-info-count">{countData.customers}</div>
                                        </div>
                                    </Link>
                                ) : null}
                                {user.role.modules.some((mod) => mod.value === "inventory" && mod.submodules.includes("groups")) ? (
                                    <Link to="inventory/groups" className="menu-link">
                                        <div className={"menu-sub-item" + (adminLocation.includes("inventory/groups") ? " active" : "")}>
                                            <span>Groupes</span>
                                            <div className="sub-item-info-count">{countData.groups}</div>
                                        </div>
                                    </Link>
                                ) : null}
                                {user.role.modules.some((mod) => mod.value === "inventory" && mod.submodules.includes("support")) ? (
                                    <Link to="inventory/support" className="menu-link">
                                        <div className={"menu-sub-item" + (adminLocation.includes("inventory/support") ? " active" : "")}>
                                            <span>Support</span>
                                        </div>
                                    </Link>
                                ) : null}
                                {user.role.modules.some((mod) => mod.value === "inventory" && mod.submodules.includes("domains")) ? (
                                    <Link to="inventory/domains" className="menu-link">
                                        <div className={"menu-sub-item" + (adminLocation.includes("inventory/domains") ? " active" : "")}>
                                            <span>Gestion NDD</span>
                                        </div>
                                    </Link>
                                ) : null}
                            </div>
                        ) : null}
                        {user.role.modules.some((mod) => mod.value === "projects") ? (
                            <div>
                                <div className="menu-item projects has-sub-items">
                                    <span>Projets</span>
                                </div>
                                {user.role.modules.some((mod) => mod.value === "projects" && mod.submodules.includes("overview")) ? (
                                    <Link to="projects/overview" className="menu-link">
                                        <div className={"menu-sub-item" + (adminLocation.includes("projects/overview") ? " active" : "")}>
                                            <span>Suivi équipe</span>
                                        </div>
                                    </Link>
                                ) : null}
                                {user.role.modules.some((mod) => mod.value === "projects" && mod.submodules.includes("tracking")) ? (
                                    <Link to="projects/tracking" className="menu-link">
                                        <div className={"menu-sub-item" + (adminLocation.includes("projects/tracking") ? " active" : "")}>
                                            <span>Saisie d'activité</span>
                                        </div>
                                    </Link>
                                ) : null}
                            </div>
                        ) : null}
                        {user.role.modules.some((mod) => mod.value === "hiring") ? (
                            <div>
                                <div className="menu-item tests has-sub-items">
                                    <span>Recrutement</span>
                                </div>
                                {user.role.modules.some((mod) => mod.value === "hiring" && mod.submodules.includes("tests")) ? (
                                    <Link to="hiring/tests" className="menu-link">
                                        <div className={"menu-sub-item" + (adminLocation.includes("hiring/tests") ? " active" : "")}>
                                            <span>Tests</span>
                                            <div className="sub-item-info-count">{countData.tests}</div>
                                        </div>
                                    </Link>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                    <div className="c-main__sidebar-container__footer">
                        <VersionDisplay />
                    </div>
                </div>
            </nav>
            <div className="c-main__content">
                <div className="c-main__navbar">
                    <button className="button-large return-button" onClick={() => navigate(-1)}>
                        <ArrowLeftIcon size={16} weight="bold" />
                    </button>
                    <div className="c-searchbar">
                        <div className="input-container">
                            <CustomSearchInput
                                ref={searchInputRef}
                                Option={CustomNavbarOption}
                                placeholder={null}
                                labelType="customer"
                                entity={searchData}
                                onChange={handleSearchbarSelect}
                                value={null}
                            />
                            <div className="search-shortcut">
                                <span>⌘</span>
                                <span>K</span>
                            </div>
                        </div>
                    </div>
                    <ChristmasToggle />
                    <div className="c-divider"></div>
                    <div className="user-info-container">
                        <Tippy 
                            content={popover} 
                            allowHTML={true} 
                            placement={"bottom-end"} 
                            offset={[0, 10]} 
                            visible={popoverVisible} 
                            onClickOutside={hide} 
                            interactive={true} 
                            appendTo={"parent"}
                            animation="scale-subtle"
                            duration={[200, 150]}
                        >
                            <div className="user-item" onClick={popoverVisible ? hide : show}>
                                {user && user.backgroundColor && (
                                    <div
                                        className="member-icon"
                                        style={{
                                            backgroundColor: user.backgroundColor,
                                        }}
                                    >
                                        {user.firstName.charAt(0)}
                                        {user.lastName.charAt(0)}
                                    </div>
                                )}
                                <span>{user ? user.firstName + " " + user.lastName : <CircularProgress size={20} />}</span>
                                <CaretDownIcon size={16} weight="bold" />
                            </div>
                        </Tippy>
                    </div>
                </div>
                <div className="c-app">
                    <Routes>
                        <Route path="accounting/*" element={<Accounting />} />
                        <Route path="hiring/*" element={<Hiring />} />
                        <Route path="users/*" element={<UsersInfos />} />
                        <Route path="roles/*" element={<Roles />} />
                        <Route path="projects/*" element={<Tracking />} />
                        <Route path="inventory/*" element={<Inventory />} />
                        <Route path="commerce/*" element={<Commerce />} />
                        <Route path="*" element={<Navigate to={getDefaultRoute(user.role.modules)} />} />
                    </Routes>
                </div>
            </div>
        </main>
    ) : (
        <div className="loader-container absolute">
            <CircularProgress size={30} />
        </div>
    );
};

export default Dashboard;
